/* Tesla Industries theme — interaction layer.
   Progressive enhancement: every feature works as plain HTML first. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const isDesktop = window.matchMedia('(min-width: 1024px)');

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------- Money ---------- */
  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2);
      }
    });
  };

  /* ---------- Scroll lock ---------- */
  const lockScroll = (on) => document.body.classList.toggle('has-overlay', on);

  /* ==========================================================
     Header: flyouts, search, mobile menu
     ========================================================== */
  const initHeader = (root = document) => {
    const header = $('[data-header]', root);
    if (!header || header.dataset.ready) return;
    header.dataset.ready = 'true';

    const flyouts = $$('[data-flyout]', header);
    const scrim = $('[data-nav-scrim]', header);
    let openId = null;
    let lastTrigger = null;
    let hoverTimer;
    let leaveTimer;

    const controlsFor = (id) => $$(`[data-flyout-toggle="${id}"], [data-flyout-trigger="${id}"]`, header);

    const setExpanded = (id, value) => {
      controlsFor(id).forEach((el) => {
        if (el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', String(value));
      });
    };

    const closeFlyout = ({ restoreFocus = false } = {}) => {
      if (!openId) return;
      const panel = document.getElementById(openId);
      panel?.classList.remove('is-open');
      setExpanded(openId, false);
      header.classList.remove('is-open');
      openId = null;
      if (restoreFocus && lastTrigger) lastTrigger.focus();
    };

    const openFlyout = (id, trigger) => {
      if (openId === id) return;
      if (openId) {
        const prev = document.getElementById(openId);
        prev?.classList.remove('is-open');
        setExpanded(openId, false);
      }
      const panel = document.getElementById(id);
      if (!panel) return;
      panel.classList.add('is-open');
      setExpanded(id, true);
      header.classList.add('is-open');
      openId = id;
      lastTrigger = trigger || lastTrigger;
      if (panel.hasAttribute('data-search-panel')) {
        window.setTimeout(() => $('[data-search-input]', panel)?.focus(), 60);
      }
    };

    // Pointer: open on hover with a little intent delay
    $$('[data-flyout-trigger]', header).forEach((link) => {
      const id = link.dataset.flyoutTrigger;
      link.addEventListener('pointerenter', () => {
        if (!canHover.matches) return;
        window.clearTimeout(leaveTimer);
        window.clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => openFlyout(id, link), openId ? 0 : 120);
      });
      link.addEventListener('pointerleave', () => window.clearTimeout(hoverTimer));
      // Touch on large screens: first tap opens, second follows the link
      link.addEventListener('click', (event) => {
        if (canHover.matches) return;
        if (openId !== id) {
          event.preventDefault();
          openFlyout(id, link);
        }
      });
    });

    // Plain links without a panel close any open panel on hover
    $$('.nav__link:not([data-flyout-trigger])', header).forEach((link) => {
      link.addEventListener('pointerenter', () => {
        if (!canHover.matches || !openId) return;
        if (document.getElementById(openId)?.hasAttribute('data-search-panel')) return;
        hoverTimer = window.setTimeout(() => closeFlyout(), 120);
      });
    });

    flyouts.forEach((panel) => {
      panel.addEventListener('pointerenter', () => window.clearTimeout(leaveTimer));
    });

    header.addEventListener('pointerleave', () => {
      if (!canHover.matches || !openId) return;
      if (document.getElementById(openId)?.hasAttribute('data-search-panel')) return;
      leaveTimer = window.setTimeout(() => closeFlyout(), 220);
    });

    // Keyboard and click toggles
    $$('[data-flyout-toggle]', header).forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.flyoutToggle;
        if (openId === id) closeFlyout();
        else {
          closeMobileMenu();
          openFlyout(id, btn);
        }
      });
    });

    scrim?.addEventListener('click', () => {
      closeFlyout();
      closeMobileMenu();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (openId) closeFlyout({ restoreFocus: true });
      if (menuOpen) closeMobileMenu({ restoreFocus: true });
    });

    // Close a keyboard-opened panel when focus leaves the header
    header.addEventListener('focusout', (event) => {
      if (!openId) return;
      if (event.relatedTarget && !header.contains(event.relatedTarget)) closeFlyout();
    });

    /* ----- Mobile menu ----- */
    const menu = $('[data-mobile-menu]', header);
    const toggle = $('[data-menu-toggle]', header);
    const label = $('[data-menu-label]', header);
    let menuOpen = false;

    const showPanel = (panelId) => {
      if (!menu) return;
      const root = $('[data-panel="root"]', menu);
      $$('.mobile-menu__panel', menu).forEach((p) => p.classList.remove('is-active', 'is-behind'));
      $$('[data-panel-open]', menu).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (!panelId) {
        root.classList.add('is-active');
        return;
      }
      const panel = document.getElementById(panelId);
      root.classList.add('is-behind');
      panel.classList.add('is-active');
      $(`[data-panel-open="${panelId}"]`, menu)?.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => $('a, button', panel)?.focus(), 40);
    };

    const openMobileMenu = () => {
      if (!menu) return;
      closeFlyout();
      menuOpen = true;
      menu.classList.add('is-open');
      header.classList.add('is-open');
      toggle.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = label.dataset.close;
      lockScroll(true);
      showPanel(null);
      window.setTimeout(() => $('.mobile-menu__link', menu)?.focus(), 80);
    };

    const closeMobileMenu = ({ restoreFocus = false } = {}) => {
      if (!menu || !menuOpen) return;
      menuOpen = false;
      menu.classList.remove('is-open');
      header.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (label) label.textContent = label.dataset.open;
      lockScroll(false);
      if (restoreFocus) toggle.focus();
    };

    toggle?.addEventListener('click', () => (menuOpen ? closeMobileMenu() : openMobileMenu()));

    menu?.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-panel-open]');
      if (opener) {
        showPanel(opener.dataset.panelOpen);
        return;
      }
      const back = event.target.closest('[data-panel-back]');
      if (back) {
        const fromId = back.closest('[data-panel]').id;
        showPanel(null);
        window.setTimeout(() => $(`[data-panel-open="${fromId}"]`, menu)?.focus(), 40);
      }
    });

    // Keep focus inside the open mobile menu
    menu?.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !menuOpen) return;
      const active = $('.mobile-menu__panel.is-active', menu);
      const focusables = $$('a[href], button:not([disabled])', active);
      const all = [toggle, ...focusables];
      const first = all[0];
      const last = all[all.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    isDesktop.addEventListener('change', (e) => {
      if (e.matches) closeMobileMenu();
      else closeFlyout();
    });

    /* ----- Predictive search ----- */
    const searchPanel = $('[data-search-panel]', header);
    if (searchPanel) {
      const input = $('[data-search-input]', searchPanel);
      const results = $('[data-search-results]', searchPanel);
      const initial = results.innerHTML;
      let controller;

      const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

      const render = (data, terms) => {
        const products = data?.resources?.results?.products || [];
        const pages = data?.resources?.results?.pages || [];
        if (!products.length && !pages.length) {
          results.innerHTML = `<p class="t-small t-muted">${escapeHtml(theme.strings.noResults)}</p>`;
          return;
        }
        let html = '';
        if (pages.length) {
          html += '<ul role="list">';
          html += pages.map((p) => `<li><a href="${p.url}"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg>${escapeHtml(p.title)}</a></li>`).join('');
          html += '</ul>';
        }
        if (products.length) {
          html += `<ul role="list" class="${pages.length ? 'mt-3' : ''}">`;
          html += products.map((p) => {
            const img = p.featured_image?.url || p.image;
            const thumb = img ? `<img src="${img}${img.includes('?') ? '&' : '?'}width=96" alt="" width="40" height="40" loading="lazy">` : '';
            return `<li><a href="${p.url}">${thumb}<span>${escapeHtml(p.title)}<br><span class="t-caption t-muted" style="font-weight:400">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></span></a></li>`;
          }).join('');
          html += '</ul>';
        }
        html += `<p class="mt-3"><a class="link" href="${theme.routes.root.replace(/\/$/, '')}/search?q=${encodeURIComponent(terms)}">${escapeHtml(theme.strings.searchAll || 'See all results')}</a></p>`;
        results.innerHTML = html;
      };

      input?.addEventListener('input', debounce(async () => {
        const terms = input.value.trim();
        if (!terms) {
          results.innerHTML = initial;
          return;
        }
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${theme.routes.predictiveSearch}.json?q=${encodeURIComponent(terms)}&resources[type]=product,page&resources[limit]=5&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } });
          if (!res.ok) throw new Error(res.statusText);
          render(await res.json(), terms);
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = initial;
        }
      }, 180));
    }
  };

  /* ==========================================================
     Registry and boot — feature files add their init functions here
     ========================================================== */
  const inits = [initHeader];
  let booted = false;
  const safely = (fn, root) => {
    try { fn(root); } catch (err) { console.error('[theme]', err); }
  };
  const run = (root = document) => inits.forEach((fn) => safely(fn, root));

  window.Theme = Object.assign(window.Theme || {}, {
    $, $$, debounce, announce, formatMoney, lockScroll,
    prefersReducedMotion, canHover, isDesktop,
    // Features registered after boot (e.g. a later deferred script) start right away
    register(fn) {
      inits.push(fn);
      if (booted) safely(fn, document);
    },
  });

  const boot = () => {
    if (booted) return;
    booted = true;
    run();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else window.setTimeout(boot, 0);

  // Theme editor support
  document.addEventListener('shopify:section:load', (event) => run(event.target));
})();